import React, { useState, useEffect } from "react";
import { LegalLayout, P, Strong, UL, LI, Callout, Table } from "./LegalLayout";

export default function CookiePolicy() {
  const [lang, setLang] = useState(() => localStorage.getItem("nexum-lang") || "es");
  useEffect(() => {
    localStorage.setItem("nexum-lang", lang);
    window.scrollTo(0, 0);
  }, [lang]);

  const content = {
    es: {
      title: "Política de Cookies",
      subtitle:
        "Utilizamos cookies con moderación y por buenas razones. Aquí explicamos exactamente cuáles, por qué y cómo puedes revocar tu consentimiento en cualquier momento.",
      lastUpdated: "Marzo 2026",
      sections: [
        {
          id: "que-son",
          heading: "¿Qué son las cookies?",
          body: (
            <>
              <P>
                Una <Strong>cookie</Strong> es un pequeño fichero de texto que un sitio web almacena en el navegador del usuario. Se utiliza para recordar información entre visitas, mantener sesiones activas, medir el uso del sitio o personalizar la experiencia.
              </P>
              <P>
                Esta política sigue las <Strong>directrices de la AEPD</Strong> (Guía sobre uso de las cookies) y cumple con el artículo 22.2 de la LSSI-CE, así como con el RGPD.
              </P>
            </>
          ),
        },
        {
          id: "tipos",
          heading: "Tipos de cookies según su finalidad",
          body: (
            <>
              <P>Clasificamos las cookies según su finalidad y su duración:</P>
              <UL>
                <LI><Strong>Técnicas:</Strong> imprescindibles para el funcionamiento del sitio. No requieren consentimiento.</LI>
                <LI><Strong>De preferencias:</Strong> recuerdan opciones del usuario (idioma, región).</LI>
                <LI><Strong>De análisis o medición:</Strong> permiten cuantificar el uso agregado del sitio para mejorarlo.</LI>
                <LI><Strong>De marketing o publicidad:</Strong> permiten mostrar anuncios personalizados. Nexum <Strong>no utiliza</Strong> cookies publicitarias.</LI>
              </UL>
              <P>Según su duración, pueden ser:</P>
              <UL>
                <LI><Strong>De sesión:</Strong> caducan al cerrar el navegador.</LI>
                <LI><Strong>Persistentes:</Strong> permanecen almacenadas durante un plazo definido.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "cookies-usadas",
          heading: "Cookies utilizadas en este sitio",
          body: (
            <>
              <Table
                headers={["Nombre", "Titular", "Finalidad", "Tipo", "Duración"]}
                rows={[
                  ["nexum-lang", "Nexum", "Recordar el idioma seleccionado por el usuario", "Técnica / Preferencia", "1 año"],
                  ["nexum-cookie-consent", "Nexum", "Almacenar el consentimiento del usuario respecto a esta política", "Técnica", "12 meses"],
                  ["ph_*", "PostHog", "Analítica de producto anonimizada (sin publicidad)", "Análisis", "12 meses"],
                  ["_ga / _ga_*", "Google (si activa)", "Estadística agregada de visitas con IP anonimizada", "Análisis (terceros)", "24 meses"],
                ]}
              />
              <Callout>
                Las cookies analíticas están configuradas con <Strong>anonimización de IP</Strong> y solo se activan tras tu consentimiento explícito en el banner.
              </Callout>
            </>
          ),
        },
        {
          id: "consentimiento",
          heading: "Consentimiento",
          body: (
            <>
              <P>
                Cuando accedes por primera vez al sitio te mostramos un <Strong>banner de configuración</Strong> desde el que puedes:
              </P>
              <UL>
                <LI><Strong>Aceptar todas</Strong> las cookies.</LI>
                <LI><Strong>Rechazar</Strong> las no esenciales (equivalente a rechazo total salvo las técnicas).</LI>
                <LI><Strong>Personalizar</Strong> tu elección por categoría.</LI>
              </UL>
              <P>
                Tu decisión se almacena durante 12 meses. Transcurrido ese plazo, volveremos a solicitar consentimiento.
              </P>
            </>
          ),
        },
        {
          id: "gestion",
          heading: "Cómo gestionar o eliminar cookies",
          body: (
            <>
              <P>
                Puedes revocar tu consentimiento y eliminar las cookies desde la configuración de tu navegador:
              </P>
              <UL>
                <LI><Strong>Chrome:</Strong> Configuración → Privacidad y seguridad → Cookies.</LI>
                <LI><Strong>Safari:</Strong> Preferencias → Privacidad.</LI>
                <LI><Strong>Firefox:</Strong> Opciones → Privacidad y seguridad.</LI>
                <LI><Strong>Edge:</Strong> Configuración → Cookies y permisos del sitio.</LI>
              </UL>
              <P>
                Ten en cuenta que la desactivación de cookies técnicas puede afectar al funcionamiento del sitio.
              </P>
            </>
          ),
        },
        {
          id: "terceros",
          heading: "Cookies de terceros",
          body: (
            <P>
              Algunas cookies son gestionadas por proveedores externos que actúan como responsables autónomos del tratamiento. Al aceptarlas, aceptas también las políticas del proveedor correspondiente. Puedes consultar los detalles y ejercer tus derechos directamente ante ellos.
            </P>
          ),
        },
        {
          id: "cambios",
          heading: "Cambios en la política de cookies",
          body: (
            <P>
              Actualizaremos esta política cuando cambien las cookies utilizadas o la legislación aplicable. La versión vigente será siempre la publicada en esta URL, indicándose la fecha en la cabecera del documento.
            </P>
          ),
        },
      ],
    },
    en: {
      title: "Cookie Policy",
      subtitle:
        "We use cookies sparingly and for good reasons. This document explains exactly which ones, why, and how you can withdraw your consent at any time.",
      lastUpdated: "March 2026",
      sections: [
        {
          id: "que-son",
          heading: "What are cookies?",
          body: (
            <>
              <P>
                A <Strong>cookie</Strong> is a small text file that a website stores in the user's browser. It is used to remember information between visits, keep sessions active, measure site usage, or personalize experience.
              </P>
              <P>
                This policy follows the <Strong>AEPD guidelines</Strong> on cookies and complies with article 22.2 of Spanish LSSI-CE and with the GDPR.
              </P>
            </>
          ),
        },
        {
          id: "tipos",
          heading: "Types of cookies by purpose",
          body: (
            <>
              <P>Cookies are classified by purpose and duration:</P>
              <UL>
                <LI><Strong>Technical:</Strong> essential for the site to work. No consent required.</LI>
                <LI><Strong>Preferences:</Strong> remember user options (language, region).</LI>
                <LI><Strong>Analytics:</Strong> measure aggregated site usage to improve it.</LI>
                <LI><Strong>Marketing / advertising:</Strong> serve personalized ads. Nexum <Strong>does not use</Strong> advertising cookies.</LI>
              </UL>
              <P>By duration:</P>
              <UL>
                <LI><Strong>Session:</Strong> expire when the browser closes.</LI>
                <LI><Strong>Persistent:</Strong> stored for a defined period.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "cookies-usadas",
          heading: "Cookies used on this site",
          body: (
            <>
              <Table
                headers={["Name", "Owner", "Purpose", "Type", "Duration"]}
                rows={[
                  ["nexum-lang", "Nexum", "Remember the language selected by the user", "Technical / Preference", "1 year"],
                  ["nexum-cookie-consent", "Nexum", "Store the user's consent regarding this policy", "Technical", "12 months"],
                  ["ph_*", "PostHog", "Anonymized product analytics (no advertising)", "Analytics", "12 months"],
                  ["_ga / _ga_*", "Google (if enabled)", "Aggregated visit statistics with anonymized IP", "Analytics (third-party)", "24 months"],
                ]}
              />
              <Callout>
                Analytics cookies are configured with <Strong>IP anonymization</Strong> and are only activated after your explicit consent on the banner.
              </Callout>
            </>
          ),
        },
        {
          id: "consentimiento",
          heading: "Consent",
          body: (
            <>
              <P>The first time you visit the site we show a <Strong>configuration banner</Strong> from which you can:</P>
              <UL>
                <LI><Strong>Accept all</Strong> cookies.</LI>
                <LI><Strong>Reject</Strong> non-essential ones (equivalent to total rejection except for technical cookies).</LI>
                <LI><Strong>Customize</Strong> your choice by category.</LI>
              </UL>
              <P>Your decision is stored for 12 months. After that, we will ask again.</P>
            </>
          ),
        },
        {
          id: "gestion",
          heading: "How to manage or delete cookies",
          body: (
            <>
              <P>You can withdraw your consent and delete cookies from your browser settings:</P>
              <UL>
                <LI><Strong>Chrome:</Strong> Settings → Privacy and security → Cookies.</LI>
                <LI><Strong>Safari:</Strong> Preferences → Privacy.</LI>
                <LI><Strong>Firefox:</Strong> Options → Privacy & Security.</LI>
                <LI><Strong>Edge:</Strong> Settings → Cookies and site permissions.</LI>
              </UL>
              <P>Please note that disabling technical cookies may affect the functioning of the site.</P>
            </>
          ),
        },
        {
          id: "terceros",
          heading: "Third-party cookies",
          body: (
            <P>
              Some cookies are managed by third parties acting as independent controllers. By accepting them, you also accept the corresponding provider's policies. You can consult the details and exercise your rights directly with them.
            </P>
          ),
        },
        {
          id: "cambios",
          heading: "Changes to the cookie policy",
          body: (
            <P>
              We will update this policy whenever the cookies used or applicable legislation change. The version in force will always be the one published at this URL, and the date shown in the document header.
            </P>
          ),
        },
      ],
    },
  };

  const c = content[lang];
  return (
    <LegalLayout
      lang={lang}
      setLang={setLang}
      meta={{ title: c.title, subtitle: c.subtitle, lastUpdated: c.lastUpdated }}
      sections={c.sections}
      related={[
        { href: "/privacidad", label: lang === "es" ? "Política de Privacidad" : "Privacy Policy" },
        { href: "/terminos", label: lang === "es" ? "Términos de Uso" : "Terms of Use" },
        { href: "/legal", label: lang === "es" ? "Aviso Legal" : "Legal Notice" },
      ]}
    />
  );
}
